import clsx from "clsx";
import type { SVGProps } from "react";

/** How a part of an icon is drawn besides the plain line in the text's colour: coloured only where the colour means something (rising / falling as the candles, the accent for a highlight). */
type Tone = "good" | "bad" | "accent" | "goodFill" | "badFill" | "solid" | "soft" | "goodSoft" | "badSoft" | "accentSoft";

const TONE: Record<Tone, SVGProps<SVGPathElement>> = {
  good: { stroke: "var(--good)" },
  bad: { stroke: "var(--bad)" },
  accent: { stroke: "var(--accent)" },
  goodFill: { fill: "var(--good)", stroke: "none" },
  badFill: { fill: "var(--bad)", stroke: "none" },
  solid: { fill: "currentColor", stroke: "none" },
  soft: { fill: "currentColor", fillOpacity: 0.18, stroke: "none" },
  goodSoft: { fill: "var(--good)", fillOpacity: 0.25, stroke: "none" },
  badSoft: { fill: "var(--bad)", fillOpacity: 0.25, stroke: "none" },
  accentSoft: { fill: "var(--accent)", fillOpacity: 0.28, stroke: "none" },
};

type Part = string | readonly [Tone, string];

/**
 * The platform's own icon set (24×24, stroke 1.75 — as fine as the chart's lines), drawn for Illustrading: drawing
 * tools show what they draw (handles as in the chart, the position's long and short area), the market tab its long and
 * short candles. A part is a line in the text's colour or a toned one (`TONE`). Icons are decorative (`aria-hidden`);
 * the text next to them carries meaning.
 */
const PATHS = {
  star: ["M12 3.5l2.6 5.3 5.9.85-4.25 4.15 1 5.85L12 16.9l-5.25 2.75 1-5.85L3.5 9.65l5.9-.85z"],
  market: [["good", "M8 3v3"], ["good", "M8 16v3"], ["goodFill", "M6.25 6h3.5v10h-3.5z"], ["bad", "M16 6v3"], ["bad", "M16 18v3"], ["badFill", "M14.25 9h3.5v9h-3.5z"]],
  news: ["M6 4h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z", "M8 8h8", ["accent", "M7 14.5h2.5L11 11l2 6.5 1.5-3H17"]],
  clock: [["accentSoft", "M12 12V3a9 9 0 0 1 7.8 4.5z"], "M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z", "M12 7.5V12l3 2"],
  vote: ["M4 13h16v7H4z", "M8 13V4h8v9", ["good", "M10 8.75l1.5 1.5L14.25 7.5"]],
  sliders: ["M4 6h9", "M19 6h1", "M18.25 6a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0z", "M4 12h2", "M12 12h8", "M11.25 12a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0z", "M4 18h8", "M18 18h2", "M17.25 18a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0z"],
  layers: [["accentSoft", "m12 3 9 4.5-9 4.5-9-4.5z"], "m12 3 9 4.5-9 4.5-9-4.5z", "m3 12 9 4.5 9-4.5", "m3 16.5 9 4.5 9-4.5"],
  grid: ["M5 4h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z", "M12 4v16", "M4 12h16"],
  crosshair: ["M12 3v6", "M12 15v6", "M3 12h6", "M15 12h6", ["solid", "M13.1 12a1.1 1.1 0 1 1-2.2 0 1.1 1.1 0 0 1 2.2 0z"]],
  magnet: ["M5 4h4v8a3 3 0 0 0 6 0V4h4v8a7 7 0 0 1-14 0z", ["accent", "M5 8h4"], ["accent", "M15 8h4"]],
  pencil: ["M4 20h4L19 9l-4-4L4 16z", "m13 7 4 4"],
  lock: ["M7 11h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1z", "M9 11V8a3 3 0 0 1 6 0v3", "M12 15v1.5"],
  unlock: ["M7 11h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1z", "M9 11V8a3 3 0 0 1 5.8-1.1", "M12 15v1.5"],
  maximize: ["M4 9V4h5", "M15 4h5v5", "M20 15v5h-5", "M9 20H4v-5"],
  minimize: ["M9 4v5H4", "M15 4v5h5", "M20 15h-5v5", "M4 15h5v5"],
  target: ["M18 12a6 6 0 1 1-12 0 6 6 0 0 1 12 0z", ["solid", "M13.5 12a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"], "M12 3v3", "M12 18v3", "M3 12h3", "M18 12h3"],
  alert: ["M12 3.5 21.5 20h-19z", "M12 10v4", "M12 17h.01"],
  cursor: ["M6 3v15l4-3.5 2.75 6 2.25-1-2.75-6H18z"],
  hline: ["M3 12h2", "M9 12h6", "M19 12h2", "M9 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0z", "M19 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"],
  hray: ["M9 12h12", "m18 9 3 3-3 3", "M8 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"],
  vline: ["M12 3v7", "M12 14v7", "M14 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"],
  trend: ["M7.4 16.6l9.2-9.2", "M8 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0z", "M20 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"],
  zone: [["soft", "M4 7h16v10H4z"], "M4 7h16v10H4z"],
  position: [["goodSoft", "M4 4h16v7H4z"], ["good", "M4 4h16v7H4z"], ["badSoft", "M4 13h16v7H4z"], ["bad", "M4 13h16v7H4z"], "M2 12h20"],
  range: ["M6 3h12", "M6 21h12", "M12 6v12", "m9 9 3-3 3 3", "m9 15 3 3 3-3"],
  fibRetracement: ["M3 4h18", "M3 10h18", "M3 14h18", "M3 20h18", ["accent", "M7 18 17 6"]],
  fibExtension: ["M3 20h18", "M3 14h18", "M3 9h12", "M3 4h12", ["accent", "M19 14V4"], ["accent", "m16.5 6.5 2.5-2.5 2.5 2.5"]],
  fibTrend: [["accent", "M3 19 7 9l4 6"], "M14 4h7", "M14 9h7", "M14 14h7", "M14 19h7"],
  fibTime: ["M3 4v16", "M5.5 4v16", "M8 4v16", "M13 4v16", "M20.5 4v16"],
  pen: ["M3 17c2-3 4-3 5.5-1s3.5 2 5-1 3-8 7-9"],
  text: ["M5 6V4h14v2", "M12 4v16", "M9 20h6"],
  undo: ["M9 14 4 9l5-5", "M4 9h10a6 6 0 0 1 0 12h-3"],
  trash: ["M4 6h16", "M10 3h4", "M6 6l1 14h10l1-14", "M10 10v6", "M14 10v6"],
  up: ["M12 19V5", "m6 11 6-6 6 6"],
  down: ["M12 5v14", "m6 13 6 6 6-6"],
  user: ["M16 8a4 4 0 1 1-8 0 4 4 0 0 1 8 0z", "M4 20c0-3.3 3.6-6 8-6s8 2.7 8 6"],
  settings: ["M10.11 4.95L10.51 2.62L13.49 2.62L13.89 4.95A7.3 7.3 0 0 1 15.65 5.68L17.58 4.31L19.69 6.42L18.32 8.35A7.3 7.3 0 0 1 19.05 10.11L21.38 10.51L21.38 13.49L19.05 13.89A7.3 7.3 0 0 1 18.32 15.65L19.69 17.58L17.58 19.69L15.65 18.32A7.3 7.3 0 0 1 13.89 19.05L13.49 21.38L10.51 21.38L10.11 19.05A7.3 7.3 0 0 1 8.35 18.32L6.42 19.69L4.31 17.58L5.68 15.65A7.3 7.3 0 0 1 4.95 13.89L2.62 13.49L2.62 10.51L4.95 10.11A7.3 7.3 0 0 1 5.68 8.35L4.31 6.42L6.42 4.31L8.35 5.68A7.3 7.3 0 0 1 10.11 4.95z", "M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z"],
  book: ["M3 5h6a3 3 0 0 1 3 3v12a2.5 2.5 0 0 0-2.5-2.5H3z", "M21 5h-6a3 3 0 0 0-3 3v12a2.5 2.5 0 0 1 2.5-2.5H21z"],
  logout: ["M10 4H5v16h5", "m15 8 4 4-4 4", "M19 12H9"],
  shield: ["M12 3l8 3v6c0 4.5-3.4 8-8 9-4.6-1-8-4.5-8-9V6z"],
  save: ["M5 4h11l3 3v13H5z", "M8 4v4h7V4", "M8 20v-6h8v6"],
  folderOpen: ["M3 19V5h6l2 2h7v3", "M3 19l3-9h15l-3 9z"],
  search: ["M18 11a7 7 0 1 1-14 0 7 7 0 0 1 14 0z", "m20 20-4-4"],
  info: ["M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z", "M12 11v5", "M12 7.5h.01"],
  check: ["m5 12.5 4.5 4.5L19 7"],
  plus: ["M12 5v14", "M5 12h14"],
  x: ["m6 6 12 12", "M18 6 6 18"],
  chevronDown: ["m6 9 6 6 6-6"],
  chevronRight: ["m9 6 6 6-6 6"],
  sun: ["M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0z", "M12 2.5v2", "M12 19.5v2", "M2.5 12h2", "M19.5 12h2", "m5.3 5.3 1.4 1.4", "m17.3 17.3 1.4 1.4", "m5.3 18.7 1.4-1.4", "m17.3 6.7 1.4-1.4"],
  moon: ["M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z"],
  gamepad: ["M7 8h10a4 4 0 0 1 4 4v1a3 3 0 0 1-5.4 1.8L14.5 14h-5l-1.1 1.8A3 3 0 0 1 3 13v-1a4 4 0 0 1 4-4z", "M7 10.5v3", "M5.5 12h3", "M15.5 11h.01", "M17.5 13h.01"],
} as const satisfies Record<string, readonly Part[]>;

export type IconName = keyof typeof PATHS;

function IconPart({ part }: { part: Part }) {
  if (typeof part === "string") return <path d={part} />;
  return <path d={part[1]} {...TONE[part[0]]} />;
}

export function Icon({ name, className }: { name: IconName; className?: string }) {
  const parts: readonly Part[] = PATHS[name];
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden className={clsx("inline-block shrink-0", className ?? "h-4 w-4")}>
      {/* a part may repeat a path in another tone (a filled area and its outline): the position keeps the keys apart */}
      {parts.map((p, n) => (
        <IconPart key={`${n}:${typeof p === "string" ? p : p[1]}`} part={p} />
      ))}
    </svg>
  );
}
